/**
 * PaymentDialogs Components
 * Dialogs for editing and creating employee payments
 */

import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import dayjs from 'dayjs';

/**
 * EditPaymentDialog - Modal for editing existing payment
 */
export function EditPaymentDialog({
  open,
  onClose,
  editPaymentForm,
  setEditPaymentForm,
  onSave,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Επεξεργασία Πληρωμής</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="date"
              label="Ημερομηνία"
              InputLabelProps={{ shrink: true }}
              value={editPaymentForm.payment_date}
              onChange={(e) => setEditPaymentForm({ ...editPaymentForm, payment_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Ποσό"
              value={editPaymentForm.amount}
              onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })}
              InputProps={{ startAdornment: '€' }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Τρόπος Πληρωμής</InputLabel>
              <Select
                value={editPaymentForm.payment_method}
                label="Τρόπος Πληρωμής"
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, payment_method: e.target.value })}
              >
                <MenuItem value="cash">Μετρητά</MenuItem>
                <MenuItem value="bank_transfer">Τραπεζική</MenuItem>
                <MenuItem value="check">Επιταγή</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Σημειώσεις"
              multiline
              rows={2}
              value={editPaymentForm.notes}
              onChange={(e) => setEditPaymentForm({ ...editPaymentForm, notes: e.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Ακύρωση</Button>
        <Button onClick={onSave} variant="contained">Αποθήκευση</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * NewPaymentDialog - Modal for creating new payment
 */
export function NewPaymentDialog({
  open,
  onClose,
  employeeName,
  newPaymentForm,
  setNewPaymentForm,
  unpaidWeeks,
  onSave,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Νέα Πληρωμή
        {employeeName && (
          <Typography variant="body2" color="textSecondary">
            {employeeName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="date"
              label="Ημερομηνία"
              InputLabelProps={{ shrink: true }}
              value={newPaymentForm.payment_date}
              onChange={(e) => setNewPaymentForm({ ...newPaymentForm, payment_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Ποσό"
              value={newPaymentForm.amount}
              onChange={(e) => setNewPaymentForm({ ...newPaymentForm, amount: e.target.value })}
              InputProps={{ startAdornment: '€' }}
            />
          </Grid>
          
          {unpaidWeeks.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Η πληρωμή κατανέμεται αυτόματα στις παλαιότερες απλήρωτες εβδομάδες
                </Typography>
              </Alert>
              <Typography variant="subtitle2" gutterBottom>
                Απλήρωτες: €{unpaidWeeks.reduce((sum, w) => sum + w.unpaid, 0).toFixed(2)}
              </Typography>
              <Box sx={{ maxHeight: 150, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                {unpaidWeeks.map((week, index) => {
                  const paymentAmount = parseFloat(newPaymentForm.amount) || 0;
                  const previousWeeksTotal = unpaidWeeks.slice(0, index).reduce((sum, w) => sum + w.unpaid, 0);
                  const remainingForThisWeek = Math.max(0, paymentAmount - previousWeeksTotal);
                  const willBeAllocated = Math.min(remainingForThisWeek, week.unpaid);
                  
                  return (
                    <Box 
                      key={week.week_start} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        p: 0.5,
                        bgcolor: willBeAllocated > 0 ? '#e8f5e9' : 'transparent',
                        borderRadius: 1
                      }}
                    >
                      <Typography variant="body2">
                        {dayjs(week.week_start).format('DD/MM')} - €{week.unpaid.toFixed(2)}
                      </Typography>
                      {willBeAllocated > 0 && (
                        <Chip label={`€${willBeAllocated.toFixed(2)}`} size="small" color="success" />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Τρόπος Πληρωμής</InputLabel>
              <Select
                value={newPaymentForm.payment_method}
                label="Τρόπος Πληρωμής"
                onChange={(e) => setNewPaymentForm({ ...newPaymentForm, payment_method: e.target.value })}
              >
                <MenuItem value="cash">Μετρητά</MenuItem>
                <MenuItem value="bank_transfer">Τραπεζική</MenuItem>
                <MenuItem value="check">Επιταγή</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Ακύρωση</Button>
        <Button onClick={onSave} variant="contained">Αποθήκευση</Button>
      </DialogActions>
    </Dialog>
  );
}

